
import React, { useMemo } from "react"
import { Accordion, AccordionDetails, AccordionSummary, Box, Checkbox, CircularProgress, FormControl, Grid, InputLabel, LinearProgress, ListItemText, MenuItem, OutlinedInput, Select, Table, TableBody, TableCell, TableContainer, TableRow, ToggleButton, ToggleButtonGroup } from "@mui/material"
import AuthService from "../../service"
import { useDispatch, useSelector } from "react-redux"
import SearchIcon from '@mui/icons-material/Search'
import LoadingButton from '@mui/lab/LoadingButton'
import { useSnackbar } from "notistack"
import { getAllLoadings, hasAnyLoading, setLoading } from "../../dashboard/component/loading/store"
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import CheckIcon from '@mui/icons-material/Check'
import UnidadeChips from "../unidadeChip"
import { ifAnyGranted } from "../../auth"
import { useEffect } from "react"
import { useSocket } from "../../utils/socket"
import FilterFields from "./FilterFields"
import FilterModal from "./FilterModal"
import { criarFetchMerged, RESULTADO_ERRO, RESULTADO_LOADING } from "./fetchMerged"
import { criarAvisadorDeErro } from "./avisoDeErro"
import { montarParametrosBusca } from "./parametrosBusca"
import { salvarUnidadesSelecionadas } from "./store"
import { rotuloMarca, SEM_MARCA } from "../../utils/marca"

const LOADING_LABELS = {
    KPI_REALTIME_TODAY: 'Inf. Tempo real - Seu negócio agora',
    KPI_REALTIME: 'Inf. Tempo real - Seu negócio hoje',
    KPI_IDENTIFIER: "Inf. Tempo real - Ocupação (comandas e mesas)",
    KPI_TIME_METRICS: "Inf. Tempo real - Tempos Médios",
    KPI_SALES: 'Vendas',
    KPI_PRODUCT: 'Produtos',
    KPI_CUSTOMER: 'Clientes',
    KPI_CHECKING: 'Auditoria',
    KPI_ORDER_INTEGRATION: 'Pedido integração',
    KPI_PAYMENT_EXCLUSION: 'Exclusões de pagamentos',
    KPI_PERFORMANCE_STORE: 'Performance das Unidades',
    KPI_PERFORMANCE_TIME_STATUS: 'Performance da operação',
    SEND_GENERAL_PARAMETERS: 'Parâmetros Sischef',
}

function TrademarkFilter() {
    const dispatch = useDispatch()
    const kpiSales = useSelector((state) => state.kpiSales)
    const selectedTrademarks = useSelector((state) => state.trademark.selecteds)
    const { trademarks, totalMarcasReais } = useMemo(() => {
        const set = new Set()
        let temVendaSemMarca = false

        kpiSales.listAllSales.forEach(item => {
            if (item.marcaConfiguracao) {
                set.add(item.marcaConfiguracao)
            } else {
                temVendaSemMarca = true
            }
        })

        // "Sem marca" só aparece quando há marca configurada E venda sem marca
        const options = set.size && temVendaSemMarca ? [...set, SEM_MARCA] : [...set]

        return { trademarks: options, totalMarcasReais: set.size }
    }, [kpiSales])

    const isAllTrademarksSelected = useMemo(() => trademarks.length === selectedTrademarks.length, [trademarks, selectedTrademarks])

    function updateTrademark(selecteds) {
        dispatch({ type: 'UPDATE_TRADEMARK_FILTER', trademark: { selecteds } })
    }

    function isChecked(selectedList, item) {
        return selectedList.includes(item)
    }

    function handleChange(e) {
        const newValue = e.target.value

        if (newValue.includes('all') && isAllTrademarksSelected) {
            updateTrademark([])
            return
        }

        if (newValue.includes('all')) {
            updateTrademark(trademarks)
            return
        }

        updateTrademark(newValue)
    }

    function handleChangeToggle(e) {
        const newItem = e.target.value

        if (selectedTrademarks.includes(newItem)) {
            updateTrademark(selectedTrademarks.filter(item => item !== newItem))
            return
        }

        const newValue = new Set([...selectedTrademarks, newItem])

        updateTrademark([...newValue])
    }

    // corte por marcas reais: "Sem marca" não pode trocar toggle por dropdown entre buscas
    if (totalMarcasReais < 5) {
        return (
            <ToggleButtonGroup
                size="small"
                value={selectedTrademarks}
                color="primary"
                onChange={handleChangeToggle}>
                {trademarks.map(trademark => (
                    <ToggleButton
                        key={trademark}
                        value={trademark}>
                        {rotuloMarca(trademark)}
                    </ToggleButton>
                ))}
            </ToggleButtonGroup>
        )
    }

    return (
        <FormControl>
            <InputLabel>Marcas</InputLabel>
            <Select
                multiple
                value={selectedTrademarks}
                onChange={handleChange}
                input={<OutlinedInput fullWidth label="Marcas" />}
                renderValue={(selected) => `${selected?.length || 0} marcas(s) selecionada(s)`}>
                <MenuItem key="all" value="all">
                    <Checkbox checked={isAllTrademarksSelected} />
                    <ListItemText primary="Todas" />
                </MenuItem>
                {trademarks.map(trademark => (
                    <MenuItem key={trademark} value={trademark}>
                        <Checkbox checked={isChecked(selectedTrademarks, trademark)} />
                        <ListItemText primary={rotuloMarca(trademark)} />
                    </MenuItem>
                ))}
            </Select>
        </FormControl>
    )
}

export default function Filter() {
    const { selectedDate: selectedDateTime, unidadeSelected } = useSelector(state => state.dashboardFilter)
    const lastSearchParams = useSelector(state => state.dashboardFilter.params)
    const selectedDate = useMemo(() => new Date(selectedDateTime), [selectedDateTime])
    const service = new AuthService()
    const profile = service.getProfile()
    const unidadesByProfile = profile?.unidadeList
    const hasAnalyticsPro = ifAnyGranted('ROLE_SISCHEF_ANALYTICS_PRO')
    const dispatch = useDispatch()
    const { enqueueSnackbar } = useSnackbar()
    const allLoadings = useSelector((state) => getAllLoadings(state))
    const anyLoading = useSelector((state) => hasAnyLoading(state))
    // O dialog de filtros abre ao acessar e fica aberto enquanto nenhuma busca
    // foi realizada nesta sessão. Ao buscar (ou fechar no X), o usuário passa a
    // usar a barra de filtros inline.
    const modalOpen = useSelector(state => state.progress.filterModalOpen)
    const shouldRefetch = useSelector(state => state.progress.shouldRefetch)
    const searched = useSelector(state => state.progress.searched)

    useSocket(selectedDate)

    const updateFilter = (dashboardFilter) => {
        dispatch({ type: 'UPDATE_DASHBOARD_FILTER', dashboardFilter, meta: { noBroadcast: true } })
    }

    const handleCloseModal = () => {
        // Fechou sem buscar: fica o dashboard esmaecido com o botão Buscar pulsando.
        // noBroadcast: fechar o dialog é decisão de cada janela/aba.
        dispatch({ type: 'UPDATE_PROGRESS', progress: { filterModalOpen: false }, meta: { noBroadcast: true } })
    }

    const handleChangeDateTo = e => {
        if (!e) {
            return
        }

        const date = new Date(e)

        if (Number.isNaN(date.getTime())) {
            return
        }

        updateFilter({ selectedDate: date.getTime() })
    }

    const handleChangeSelectTo = (values) => {

        if (!Array.isArray(values)) {
            salvarUnidadesSelecionadas([])
            updateFilter({ unidadeSelected: [] })
            return
        }

        const unidadesParsed = values.map((item) => {
            if (typeof item === 'object') {
                return item
            }

            if (!item || typeof item !== 'string') {
                return null
            }

            try {
                return JSON.parse(item)
            } catch (e) {
                console.error('Falha ao fazer JSON.parse:', item)
                return null
            }
        }).filter(Boolean)

        // Salva a cada mudanca para a selecao sobreviver ao F5.
        salvarUnidadesSelecionadas(unidadesParsed)
        updateFilter({ unidadeSelected: unidadesParsed })
    }

    const fetchData = (searchParams) => {
        const todayPromise = service.get('/realtime/today', searchParams)
        const realTimePromise = service.get('/realtime/realtime', searchParams)
        const settingsIdentificadorPromise = service.get('/settings/identificador', searchParams)
        const timeMetricsPromise = service.get('/realtime/time-metrics', searchParams)

        Promise.all([todayPromise, realTimePromise, settingsIdentificadorPromise, timeMetricsPromise])
            .then(([todayResp, realTimeResp, settingsIdentificadorResp, timeMetricsResp]) => {

                dispatch({ type: 'UPDATE_PROGRESS', progress: { counting: true } })

                if (timeMetricsResp === "LOADING" || todayResp === "LOADING") {
                    return
                }

                dispatch({
                    type: 'KPI_REALTIME',
                    kpiToday: todayResp,
                    kpiRealTime: realTimeResp,
                    listIdentifier: settingsIdentificadorResp,
                    kpiTimeMetrics: timeMetricsResp,
                    selectedDate,
                })
            })
            .catch(error => {
                console.error('Erro ao buscar dados de realtime ou identificador:', error)
            })
            .finally(() => {
                dispatch(setLoading('KPI_REALTIME_TODAY', false))
                dispatch(setLoading('KPI_REALTIME', false))
                dispatch(setLoading('KPI_IDENTIFIER', false))
                dispatch(setLoading('KPI_TIME_METRICS', false))
            })
    }

    useEffect(() => {
        if (!shouldRefetch) {
            return
        }

        dispatch({ type: 'UPDATE_PROGRESS', progress: { shouldRefetch: false } })

        if (!lastSearchParams) {
            return
        }

        fetchData(lastSearchParams)

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [shouldRefetch])

    /* Segunda entrada do fetch: o filtro veio do Redux (dashboardFilter.params) em vez do
     * useState local, que era destruido ao navegar para /admin/cache. */
    useEffect(() => {
        if (!lastSearchParams) {
            return
        }

        fetchData(lastSearchParams)

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [lastSearchParams])

    const avisarIndicadorComErro = useMemo(() => criarAvisadorDeErro(
        (mensagem) => enqueueSnackbar(mensagem, {
            preventDuplicate: true,
            variant: 'error',
        })
    ), [enqueueSnackbar])

    const aplicarResultado = (kpiKey, promise, onSucesso) => {
        return promise.then(result => {
            if (result === RESULTADO_LOADING) {
                return
            }

            dispatch(setLoading(kpiKey, false))

            if (result === RESULTADO_ERRO) {
                avisarIndicadorComErro(LOADING_LABELS[kpiKey] || kpiKey)
                return
            }

            onSucesso(result)
        })
    }

    const onClickBuscar = async () => {
        let status
        try {
            status = await service.get('/redis/status', [])
        } catch (err) {
            console.error('Erro ao verificar status do Redis:', err)
            enqueueSnackbar('Não foi possível verificar o servidor Redis', {
                preventDuplicate: true,
                variant: 'warning',
            })
            return
        }

        if (status[0] === "Não conectado") {
            enqueueSnackbar('Não foi possível buscar as informações, servidor Redis está offline', {
                preventDuplicate: true,
                variant: 'warning',
            })

            return
        }

        let idsUnidade = unidadeSelected.map(unidade => unidade.idUnidade).join(',')
        if (!hasAnalyticsPro) {
            idsUnidade = profile.unidade.idUnidade
        }

        if (!idsUnidade) {
            enqueueSnackbar('Nenhuma unidade selecionada', {
                preventDuplicate: true,
                variant: 'warning',
            })
            return
        }

        // Quando é o mês atual: consulta cacheada vai só até ontem (meia-noite),
        // e uma segunda consulta busca apenas o dia de hoje sem cacheKey para garantir dados frescos.
        const { params, cachedParams, todayOnlyParams } = montarParametrosBusca(selectedDate, idsUnidade)

        dispatch({ type: 'INICIAR_BUSCA', unidade: idsUnidade })

        const fetchMerged = criarFetchMerged(service, dispatch, idsUnidade)

        dispatch(setLoading('KPI_PERFORMANCE_TIME_STATUS', true))
        dispatch({ type: 'KPI_PERFORMANCE_TIME_STATUS', kpiPerformanceTimeBySatus: [], selectedDate })

        dispatch(setLoading('KPI_PERFORMANCE_STORE', true))
        dispatch({ type: 'KPI_PERFORMANCE_STORE', kpiPerformance: [], selectedDate })

        dispatch(setLoading('KPI_SALES', true))
        dispatch({ type: 'KPI_SALES', kpiSales: [], selectedDate })

        dispatch(setLoading('KPI_PRODUCT', true))
        dispatch({ type: 'KPI_PRODUCT', kpiProduct: [], selectedDate })

        dispatch(setLoading('KPI_CUSTOMER', true))
        dispatch({ type: 'KPI_CUSTOMER', kpiCustomer: [], selectedDate })

        dispatch(setLoading('KPI_REALTIME_TODAY', true))
        dispatch({ type: 'KPI_REALTIME', kpiToday: [], selectedDate })

        dispatch(setLoading('KPI_TIME_METRICS', true))
        dispatch({ type: 'KPI_REALTIME', kpiTimeMetrics: [], selectedDate })

        dispatch(setLoading('KPI_REALTIME', true))
        dispatch({ type: 'KPI_REALTIME', kpiRealTime: [], selectedDate })

        dispatch(setLoading('KPI_IDENTIFIER', true))
        dispatch({ type: 'KPI_REALTIME', listIdentifier: [], selectedDate })

        dispatch(setLoading('KPI_CHECKING', true))
        dispatch({ type: 'KPI_CHECKING', kpiChecking: [], selectedDate })

        dispatch(setLoading('KPI_ORDER_INTEGRATION', true))
        dispatch({ type: 'KPI_ORDER_INTEGRATION', kpiOrderIntegration: [], selectedDate })

        dispatch(setLoading('KPI_PAYMENT_EXCLUSION', true))
        dispatch({ type: 'KPI_PAYMENT_EXCLUSION', kpiPaymentExclusion: [], selectedDate })

        dispatch(setLoading('SEND_GENERAL_PARAMETERS', true))
        dispatch({ type: 'SEND_GENERAL_PARAMETERS', generalParameters: [], selectedDate })

        aplicarResultado(
            'KPI_PERFORMANCE_TIME_STATUS',
            fetchMerged('/performanceTimeByStatus/list', 'KPI_PERFORMANCE_TIME_STATUS', cachedParams, todayOnlyParams),
            result => dispatch({ type: 'KPI_PERFORMANCE_TIME_STATUS', kpiPerformanceTimeBySatus: result, selectedDate })
        )

        aplicarResultado(
            'KPI_PERFORMANCE_STORE',
            fetchMerged('/performanceStore/list', 'KPI_PERFORMANCE_STORE', cachedParams, todayOnlyParams),
            result => dispatch({ type: 'KPI_PERFORMANCE_STORE', kpiPerformance: result, selectedDate })
        )

        aplicarResultado(
            'KPI_SALES',
            fetchMerged('/sales/card', 'KPI_SALES', cachedParams, todayOnlyParams),
            result => dispatch({ type: 'KPI_SALES', kpiSales: result, selectedDate })
        )

        aplicarResultado(
            'KPI_PRODUCT',
            fetchMerged('/product/card', 'KPI_PRODUCT', cachedParams, todayOnlyParams),
            result => dispatch({ type: 'KPI_PRODUCT', kpiProduct: result, selectedDate })
        )

        aplicarResultado(
            'KPI_CUSTOMER',
            fetchMerged('/customer/card', 'KPI_CUSTOMER', cachedParams, todayOnlyParams),
            result => dispatch({ type: 'KPI_CUSTOMER', kpiCustomer: { listAllSalesCustomer: result, selectedDate } })
        )

        // service.get('/financialMovement/pendingList', params)
        //     .then(result => {
        //         if (result != "LOADING") {
        //             dispatch(setLoading('KPI_FINANCIAL', false));
        //             dispatch({ type: 'KPI_FINANCIAL', kpiFinancialPending: result, selectedDate });
        //         }
        //     });

        aplicarResultado(
            'KPI_CHECKING',
            fetchMerged('/checking/list', 'KPI_CHECKING', cachedParams, todayOnlyParams),
            result => dispatch({ type: 'KPI_CHECKING', kpiChecking: result, selectedDate })
        )

        aplicarResultado(
            'KPI_ORDER_INTEGRATION',
            fetchMerged('/orderIntegration/listOrderIntegration', 'KPI_ORDER_INTEGRATION', cachedParams, todayOnlyParams),
            result => dispatch({ type: 'KPI_ORDER_INTEGRATION', kpiOrderIntegration: result, selectedDate })
        )

        aplicarResultado(
            'KPI_PAYMENT_EXCLUSION',
            fetchMerged('/paymentExclusion/list', 'KPI_PAYMENT_EXCLUSION', cachedParams, todayOnlyParams),
            result => dispatch({ type: 'KPI_PAYMENT_EXCLUSION', kpiPaymentExclusion: result, selectedDate })
        )

        service.get('/generalParameters/tipo/DASHBOARD', params)
            .then(result => {
                if (result !== "LOADING") {
                    dispatch(setLoading('SEND_GENERAL_PARAMETERS', false))
                    dispatch({ type: 'SEND_GENERAL_PARAMETERS', generalParameters: result, selectedDate })
                }
            })

        updateFilter({ params })
        dispatch({ type: 'UPDATE_PROGRESS', progress: { searched: true, filterModalOpen: false } })

        try {
            localStorage.setItem('filter_bi', JSON.stringify(params))
        } catch (error) {
            console.error('Error saving to localStorage:', error)
        }

        fetchData(params)
    }

    return (
        <Box>
            <FilterModal
                open={modalOpen}
                onClose={handleCloseModal}
                onConfirm={onClickBuscar}
                selectedDate={selectedDate}
                onDateChange={handleChangeDateTo}
                hasAnalyticsPro={hasAnalyticsPro}
                unidadeList={unidadesByProfile}
                unidade={unidadeSelected.map(u => JSON.stringify({ idUnidade: u.idUnidade, nome: u.nome }))}
                onUnidadeChange={handleChangeSelectTo}
                loading={anyLoading}
            />

            <Grid container spacing={2} alignItems="center" direction="row">
                <Grid item xs={12} md>
                    <FilterFields
                        selectedDate={selectedDate}
                        onDateChange={handleChangeDateTo}
                        hasAnalyticsPro={hasAnalyticsPro}
                        unidadeList={unidadesByProfile}
                        unidade={unidadeSelected.map(u => JSON.stringify({ idUnidade: u.idUnidade, nome: u.nome }))}
                        onUnidadeChange={handleChangeSelectTo}
                        inline
                    />
                </Grid>

                <Grid item xs={12} md="auto">
                    <LoadingButton
                        variant={searched ? 'outlined' : 'contained'}
                        onClick={onClickBuscar}
                        loadingIndicator="Buscando..."
                        loadingPosition="center"
                        startIcon={<SearchIcon />}
                        loading={anyLoading}
                        size="large"
                        sx={{
                            width: { xs: '100%', md: 'auto' },
                            // Enquanto nenhuma busca foi feita, o botão pulsa para
                            // chamar a atenção do usuário.
                            ...(!searched && {
                                animation: 'pulseBuscar 1.6s ease-in-out infinite',
                                '@keyframes pulseBuscar': {
                                    '0%': { boxShadow: '0 0 0 0 rgba(25, 118, 210, 0.5)' },
                                    '70%': { boxShadow: '0 0 0 12px rgba(25, 118, 210, 0)' },
                                    '100%': { boxShadow: '0 0 0 0 rgba(25, 118, 210, 0)' },
                                },
                            }),
                        }}
                    >
                        Buscar
                    </LoadingButton>
                </Grid>
            </Grid>

            {!!unidadeSelected?.length && (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mt: 1 }}>
                    <TrademarkFilter />
                    <UnidadeChips unidades={unidadeSelected} />
                </Box>
            )}

            {anyLoading && (
                <Box mt={2}>
                    <Accordion>
                        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                            <Box display="flex" alignItems="center">
                                <Box mr={1}>
                                    <CircularProgress size={20} />
                                </Box>
                                Buscando dados... Esse processo pode levar alguns minutos.
                            </Box>
                        </AccordionSummary>
                        <AccordionDetails>
                            <TableContainer>
                                <Table>
                                    <TableBody>
                                        {Object.entries(allLoadings).map(([key, value]) => (
                                            <TableRow key={key}>
                                                <TableCell component="th" scope="row">
                                                    {LOADING_LABELS[key]}
                                                </TableCell>
                                                <TableCell align="right">
                                                    {value ? (
                                                        <LinearProgress color="secondary" />
                                                    ) : (
                                                        <CheckIcon color="success" />
                                                    )}
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </AccordionDetails>
                    </Accordion>
                </Box>
            )}
        </Box>
    )
}